<template>
    <div class="min-h-screen bg-gray-100 p-6">
        <div class="max-w-6xl mx-auto">
            <!-- Header -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-800">Dashboard Bar</h1>
                        <p class="text-gray-600">{{ currentRestaurant.name }}</p>
                    </div>
                    <div class="flex space-x-4">
                        <router-link
                                to="/restaurantselector"
                                class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                        >
                            Changer de restaurant
                        </router-link>
                        <Notifications />
                    </div>
                </div>
            </div>

            <!-- Orders -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Pending Orders -->
                <div class="bg-white rounded-lg shadow-md p-6">
                    <h2 class="text-xl font-semibold mb-4 text-orange-600">En Attente</h2>
                    <div v-if="pendingOrders.length === 0" class="text-gray-500 text-center py-8">
                        Aucune commande en attente
                    </div>
                    <div v-else class="space-y-4">
                        <div
                                v-for="order in pendingOrders"
                                :key="order.id"
                                class="border border-orange-200 rounded-lg p-4"
                        >
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">Table {{ getTableNumber(order.tableId) }}</p>
                                    <p class="text-sm text-gray-600">Commande #{{ order.id }}</p>
                                </div>
                                <span class="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                  En attente
                </span>
                            </div>

                            <div class="space-y-2">
                                <div
                                        v-for="item in getBarItems(order)"
                                        :key="item.id"
                                        class="flex justify-between items-center p-2 bg-orange-50 rounded"
                                >
                                    <span>{{ item.name }} x{{ item.quantity }}</span>
                                    <button
                                            @click="startPreparation(order.id, item.id)"
                                            class="bg-info text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors"
                                    >
                                        Commencer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- In Progress Orders -->
                <div class="bg-white rounded-lg shadow-md p-6">
                    <h2 class="text-xl font-semibold mb-4 text-blue-600">En Préparation</h2>
                    <div v-if="inProgressOrders.length === 0" class="text-gray-500 text-center py-8">
                        Aucune commande en préparation
                    </div>
                    <div v-else class="space-y-4">
                        <div
                                v-for="order in inProgressOrders"
                                :key="order.id"
                                class="border border-blue-200 rounded-lg p-4"
                        >
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">Table {{ getTableNumber(order.tableId) }}</p>
                                    <p class="text-sm text-gray-600">Commande #{{ order.id }}</p>
                                </div>
                                <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  En préparation
                </span>
                            </div>

                            <div class="space-y-2">
                                <div
                                        v-for="item in getBarItems(order)"
                                        :key="item.id"
                                        class="flex justify-between items-center p-2 bg-blue-50 rounded"
                                >
                                    <span>{{ item.name }} x{{ item.quantity }}</span>
                                    <button
                                            @click="markAsReady(order.id, item.id)"
                                            class="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                                    >
                                        Prêt
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Ready Orders -->
                <div class="bg-white rounded-lg shadow-md p-6">
                    <h2 class="text-xl font-semibold mb-4 text-green-600">Prêt à Servir</h2>
                    <div v-if="readyOrders.length === 0" class="text-gray-500 text-center py-8">
                        Aucune commande prête
                    </div>
                    <div v-else class="space-y-4">
                        <div
                                v-for="order in readyOrders"
                                :key="order.id"
                                class="border border-green-200 rounded-lg p-4"
                        >
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">Table {{ getTableNumber(order.tableId) }}</p>
                                    <p class="text-sm text-gray-600">Commande #{{ order.id }}</p>
                                </div>
                                <span class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  Prêt
                </span>
                            </div>

                            <div class="space-y-2">
                                <div
                                        v-for="item in getBarItems(order)"
                                        :key="item.id"
                                        class="p-2 bg-green-50 rounded"
                                >
                                    {{ item.name }} x{{ item.quantity }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import Notifications from './notifications.vue';

    export default {
        name: 'BarDashboard',
        components: {
            Notifications
        },
        computed: {
            ...mapState(['currentRestaurant', 'orders', 'tables']),
            ...mapGetters(['barOrders']),
            pendingOrders() {
                return this.barOrders.filter(order =>
                    this.getBarItems(order).some(item => item.status === 'pending')
                );
            },
            inProgressOrders() {
                return this.barOrders.filter(order =>
                    this.getBarItems(order).some(item => item.status === 'in_progress')
                );
            },
            readyOrders() {
                return this.barOrders.filter(order =>
                    this.getBarItems(order).some(item => item.status === 'ready')
                );
            }
        },
        methods: {
            ...mapActions(['updateItemStatus']),
            getTableNumber(tableId) {
                const table = this.tables.find(t => t.id === tableId);
                return table ? table.number : 'N/A';
            },
            getBarItems(order) {
                return order.items.filter(item => item.type === 'bar' && item.status !== 'served');
            },
            startPreparation(orderId, itemId) {
                this.updateItemStatus({
                    orderId,
                    itemId,
                    status: 'in_progress',
                    station: 'bar'
                });
            },
            markAsReady(orderId, itemId) {
                this.updateItemStatus({
                    orderId,
                    itemId,
                    status: 'ready',
                    station: 'bar'
                });
            }
        }
    };
</script>